import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { CustomFieldModel } from "src/api/contractGroup/contractgroup-type";
import { Modal } from "src/api/public-types";
import { BaseButton, ContentsTitle } from "src/components";
import MetaTag from "src/components/layout/MetaTag";
import { useTabNavigation } from "src/hooks/useTabNavigation";
import { useModal } from "src/recoil/modalState/hook";
import { customFieldColumns } from "../form/columns/ContractGroupCustomFiledColumns";
import CustomFieldTable from "../form/components/CustomFieldTable";
import { useContractGroup } from "../hooks/useContractGroup";
import CustomFieldFormModal from "./components/CustomFieldFormModal";
import AdminMemoV3 from "src/components/AdminMemoV3/AdminMemoV3";
import { ServiceTypes } from "src/api/adminMemo/adminmemo-types";
import { useRecoilValue } from "recoil";
import { globalPartnerState } from "src/recoil/partners/atom";
import { CUSTOM_FIELD_TABS } from "../contractgroup-constant";
import GoToListButton from "src/components/GoToListButton";
import EditCancelButton from "src/components/EditCancelButton";
import { PagePath } from "src/pages/product/details";

const ContractGroupCustomField = () => {
  const partner = useRecoilValue(globalPartnerState);
  const [customFieldList, setCustomFieldList] = useState<CustomFieldModel[]>([]);
  const [openCustomFieldFormModal, setOpenCustomFieldFormModal] = useState<Modal>({ isOpen: false });

  const { activeTab, clickTab } = useTabNavigation({
    tabs: CUSTOM_FIELD_TABS,
  });

  const { setBaseModal } = useModal();
  const { getCtGroupCustomField, addCtGroupCustomField, deleteCtGroupCustomField, editCtGroupCustomField } = useContractGroup();

  const { handleSubmit, setValue, watch, control, reset } = useForm<CustomFieldModel>({
    defaultValues: {
      fieldId: 0, // 신규 등록인 경우, 0 전달, 수정인 경우, 초회 fieldId 재전달
      fieldName: "", // 필드명
      fieldType: "", // 필드 유형
      optionList: [
        {
          optionId: 0, // 신규 등록인 경우, 0 전달, 수정인 경우, 초기 전달 optionId 재전달
          optionName: "", // 옵션명
          orderNum: 1, // 정렬 순번
          isDeleted: false, // 삭제 여부
        },
      ],
    },
  });

  useEffect(() => {
    getCustomFieldList();
  }, []);

  // 커스텀 필드 목록 조회
  const getCustomFieldList = async () => {
    const result = await getCtGroupCustomField();
    setCustomFieldList(result);
  };

  // 커스텀 필드 상세 setValue
  const handleSetCustomFieldDetail = (detail: CustomFieldModel) => {
    Object.entries(detail).forEach(([key, value]) => {
      if (key in detail) {
        setValue(key as keyof CustomFieldModel, value);
      }
    });

    setOpenCustomFieldFormModal({ isOpen: true });
  };

  // 커스텀 필드 신규저장 / 수정 시 호출
  const handleSubmitCustomField = async (request: CustomFieldModel) => {
    let newRequest: CustomFieldModel;

    const { optionList, ...baseRequest } = request;
    //필드 등록시 정렬 순번 조회 > 배열 중 최대값 + 1

    const newFieldOrderNum = customFieldList.length > 0 ? Math.max(...customFieldList.map((item) => item.orderNum!)) + 1 : 1;

    // 단일 / 멀티 필드 일때만  optionList 전달
    if (baseRequest.fieldType === "RADIO" || baseRequest.fieldType === "CHECKBOX") {
      newRequest = {
        ...baseRequest,
        orderNum: baseRequest.fieldId === 0 ? newFieldOrderNum : baseRequest.orderNum, //
        optionList: optionList?.filter((option) => !(option.optionId === 0 && option.isDeleted)),
      };
    } else {
      // 단일 필드 일때만 전달
      newRequest = { ...baseRequest, orderNum: baseRequest.fieldId === 0 ? newFieldOrderNum : baseRequest.orderNum };
    }

    setBaseModal({
      isOpen: true,
      title: "저장하시겠습니까?",
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => {
        execute();
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
    });
    // 신규저장 / 수정 시 호출
    const execute = async () => {
      try {
        request.fieldId === 0 ? await addCtGroupCustomField(newRequest) : await editCtGroupCustomField(newRequest);
        await getCustomFieldList();
        setOpenCustomFieldFormModal({ isOpen: false });
        reset();
      } finally {
        // 실패 시 공통 에러 모달은 useApiOperation 에서 노출 - 여기서는 확인 모달만 닫아 입력값을 보존한다
        setBaseModal({ isOpen: false });
      }
    };
  };

  // 커스텀 필드 > 노출 여부 수정
  const onUpdateCustomField = async (request: CustomFieldModel | CustomFieldModel[], editType?: "orderNum" | "visible") => {
    if (editType === "visible") {
      const visibleCount = customFieldList.filter((item) => item.isVisible).length;
      const isVisible = Array.isArray(request) ? request[0].isVisible : request.isVisible;
      if (visibleCount >= 5 && isVisible) {
        setBaseModal({
          isOpen: true,
          title: "노출은 최대 5개까지 가능합니다.",
          children: <span>새로운 필드를 노출하려면, 현재 노출 중인 필드 중 하나를 비활성화 해주세요</span>,
          btnRightTitle: "확인",
          onClick: () => {
            setBaseModal({ isOpen: false });
          },
        });
        return;
      }
    }

    try {
      await editCtGroupCustomField(request);
    } finally {
      // 성공/실패 무관하게 재조회 - 실패 시 서버 상태로 토글이 원복된다
      await getCustomFieldList();
    }
  };

  // 커스텀 필드 삭제
  const onDeleteCustomField = async (request: CustomFieldModel) => {
    const execute = async () => {
      try {
        await deleteCtGroupCustomField(request);
      } finally {
        // 실패 시 공통 에러 모달은 useApiOperation 에서 노출 - 여기서는 확인 모달만 닫는다
        setBaseModal({ isOpen: false });
      }

      await getCustomFieldList();
    };
    setBaseModal({
      isOpen: true,
      title: "삭제하시겠습니까?",
      children: <span>기존 계약그룹에 등록된 필드값이 모두 삭제됩니다.</span>,
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => {
        execute();
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
    });
  };

  // validation 통과하지 못하고 error 발생시 실행
  const handleFormError = (errors: any, e?: any) => {
    e.preventDefault();
    console.log("onError errors", errors);
  };

  return (
    <div>
      <MetaTag subTitle={"계약그룹"} />
      <ContentsTitle title="" tabs={CUSTOM_FIELD_TABS} activeTab={activeTab} clickTab={clickTab} />
      <div className="contents-container__scroll">
        <div className="contents-container__wrap">
          {/* 커스텀 필드 - 컬럼 합계가 1220px 캡을 넘으므로 full-width + 테이블 가로 스크롤 */}
          <article className="contents-container__wrap-article full-width">
            <div className="contents-container__sub-title">
              <h2>커스텀 필드</h2>
              <BaseButton
                title="필드 추가"
                onClick={() => {
                  if (customFieldList.length >= 10) {
                    setBaseModal({
                      isOpen: true,
                      title: "커스텀 필드는 10개까지 등록 가능합니다.",
                      children: <span>새로운 필드를 추가하시려면 다른 필드를 삭제해주세요.</span>,
                      btnRightTitle: "확인",
                      onClick: () => {
                        setBaseModal({ isOpen: false });
                      },
                    });
                  } else {
                    setOpenCustomFieldFormModal({ isOpen: true });
                  }
                }}
              />
            </div>
            <CustomFieldTable
              columns={customFieldColumns}
              data={customFieldList}
              onUpdateCustomField={onUpdateCustomField}
              onDeleteCustomField={onDeleteCustomField}
              handleSetCustomFieldDetail={handleSetCustomFieldDetail}
            />
          </article>
          <AdminMemoV3 serviceId={Number(partner?.id)} serviceType={ServiceTypes.SERVICE_CONTRACT_GROUP_CUSTOM_FIELD} />
        </div>
      </div>
      <div className="contents-container__btn-wrap">
        <div className="left-area">
          <GoToListButton />
        </div>
      </div>
      {/* 커스텀 필드 모달 */}
      {openCustomFieldFormModal.isOpen && (
        <CustomFieldFormModal
          control={control}
          watch={watch}
          onClose={() => setOpenCustomFieldFormModal({ isOpen: false })}
          setValue={setValue}
          reset={reset}
          onSubmit={handleSubmit(handleSubmitCustomField, handleFormError)}
        />
      )}
    </div>
  );
};

export default ContractGroupCustomField;
